// Plein écran d'un graphique — Marc, 10/10, 23 h 50 : « changer la taille des graphiques au
// besoin pour le client avec la souris… si c'est plus simple, seulement une sorte d'affichage
// plein écran des graphiques… plus simple et moins dangereux ». C'est la version courte du
// lot A (CDC du 09/10, § 3) : ni taille réglable, ni mémoire, ni changement de la grille.
// La taille choisie et retrouvée à la connexion suivante viendra plus tard.
//
// LA MÊME CARTE, PAS UNE COPIE. La fenêtre n'est pas un second rendu du graphique : c'est le
// cadre de la carte qui change de rôle. Au repos il n'existe pas pour la mise en page
// (`display: contents`, la carte reste l'enfant direct de la grille) ; ouvert, il devient
// la fenêtre, fixée sur tout l'écran, et la carte y occupe la place. Aucun composant n'est
// remonté : la comparaison déjà choisie, la pyramide âge par âge, les séries décochées et la
// vue tableau restent telles quelles, et rien n'est redemandé au serveur. Un portail aurait
// remonté le graphique — et perdu tout cela.
//
// Les règles de la fenêtre sont celles des autres fenêtres de l'observatoire (« Comprendre
// ce chiffre », lien de partage) : Échap ferme, le focus entre et reste dedans, et revient au
// bouton « Agrandir » à la fermeture ; s'y ajoutent le clic sur le voile et le défilement de
// la page bloqué. Une seule fenêtre à la fois.
import { useEffect, useRef, type MouseEvent, type ReactNode, type RefObject } from 'react'
import { flushSync } from 'react-dom'
import { Maximize2, X } from 'lucide-react'
import './PleinEcran.css'

/** La fermeture de la fenêtre ouverte : en ouvrir une autre la ferme d'abord. */
let fermerOuverte: (() => void) | null = null

// Le défilement est bloqué sur `<html>` (la page défile sur le document, pas sur un
// conteneur). Un compteur plutôt qu'un booléen : quand une fenêtre en remplace une autre,
// l'ouverture de la seconde précède la fermeture de la première.
let verrous = 0
let overflowAvant = ''
function verrouillerDefilement() {
  if (verrous++ === 0) {
    overflowAvant = document.documentElement.style.overflow
    document.documentElement.style.overflow = 'hidden'
  }
}
function libererDefilement() {
  if (verrous > 0 && --verrous === 0) document.documentElement.style.overflow = overflowAvant
}

const FOCUSABLES = 'a[href], button:not([disabled]), input:not([disabled]), '
  + 'select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

/** Le focus est-il dans UNE AUTRE fenêtre (« Comprendre ce chiffre », lien de partage) ?
    Celle-là, ouverte par-dessus, garde ses touches — Échap ne ferme qu'elle. */
function dansUneAutreFenetre(el: Element | null, fenetre: HTMLElement) {
  const d = el?.closest('[role="dialog"]')
  return !!d && d !== fenetre
}

/** « Agrandir », dans la rangée de la bascule « Graphique | Tableau » : même habillage, et
    masqué dans la fenêtre — on n'agrandit pas ce qui l'est déjà, « Fermer » prend le relais. */
export function BoutonAgrandir({ titre, ouvert, onOuvrir, refBouton }: {
  titre: string; ouvert: boolean; onOuvrir: () => void; refBouton: RefObject<HTMLButtonElement | null>
}) {
  return (
    <button ref={refBouton} type="button" className="obs2-agrandir" hidden={ouvert}
      aria-haspopup="dialog" aria-label={`Agrandir le graphique ${titre}`} onClick={onOuvrir}>
      <Maximize2 size={12} aria-hidden="true" />
      Agrandir
    </button>
  )
}

/** Le cadre de la carte : transparent au repos, fenêtre plein écran une fois ouvert. */
export function CadrePleinEcran({ ouvert, titre, onFermer, retour, children }: {
  ouvert: boolean; titre: string; onFermer: () => void
  retour: RefObject<HTMLButtonElement | null>; children: ReactNode
}) {
  const fenetre = useRef<HTMLDivElement>(null)
  const barre = useRef<HTMLDivElement>(null)
  const boutonFermer = useRef<HTMLButtonElement>(null)
  // Un clic ne ferme que s'il a COMMENCÉ sur le voile : un curseur d'année glissé trop loin,
  // une sélection relâchée hors de la carte, produisent un clic sur le voile que le lecteur
  // n'a pas voulu.
  const debutSurVoile = useRef(false)

  useEffect(() => {
    if (!ouvert) return
    const f = fenetre.current
    if (!f) return
    if (fermerOuverte && fermerOuverte !== onFermer) fermerOuverte()
    fermerOuverte = onFermer
    verrouillerDefilement()
    // Le focus entre dans la fenêtre : sans ce geste, un lecteur d'écran reste sur le bouton
    // et n'apprend jamais que quelque chose s'est ouvert.
    boutonFermer.current?.focus()
    // « Agrandir » reste MONTÉ pendant l'ouverture (seulement `hidden`) : c'est le même nœud
    // qui, la fenêtre fermée, reprend le focus.
    const boutonRetour = retour.current
    let rendreFocus = true

    const touche = (e: KeyboardEvent) => {
      const actif = document.activeElement
      if (dansUneAutreFenetre(actif, f)) return
      if (e.key === 'Escape') { e.preventDefault(); onFermer(); return }
      if (e.key !== 'Tab') return
      // Le focus reste dans la fenêtre (WCAG 2.4.3 / 2.1.2) : la page derrière le voile ne
      // se voit plus, on ne doit pas pouvoir y tabuler.
      const cibles = [...f.querySelectorAll<HTMLElement>(FOCUSABLES)]
        .filter(el => !el.closest('[hidden]'))
      if (!cibles.length) return
      const premier = cibles[0]
      const dernier = cibles[cibles.length - 1]
      if (!actif || !f.contains(actif)) { e.preventDefault(); premier.focus() }
      else if (e.shiftKey && (actif === premier || actif === f)) { e.preventDefault(); dernier.focus() }
      else if (!e.shiftKey && actif === dernier) { e.preventDefault(); premier.focus() }
    }
    // Le focus envoyé AILLEURS par l'application elle-même — « Commenter » ou « Écrire à
    // TerObs » ouvrent la barre des commentaires, qui vit sous ce voile — ferme la fenêtre
    // pour laisser voir où il est allé, sans le lui reprendre.
    const sortie = (e: FocusEvent) => {
      const cible = e.target instanceof Element ? e.target : null
      if (!cible || f.contains(cible) || dansUneAutreFenetre(cible, f)) return
      rendreFocus = false
      onFermer()
    }
    // L'impression du navigateur (Ctrl+P) ferme la fenêtre AVANT sa mise en page (revue codex
    // #682, P2) : sinon le papier montrait le voile sombre fixe, et le défilement bloqué sur
    // `<html>` peut tronquer la page imprimée à son premier écran. Fermer plutôt que de
    // restyler en `@media print` : la page s'imprime telle qu'on la lit, et le verrou de
    // défilement tombe avec la fenêtre. `flushSync` : la fermeture — et le nettoyage qui
    // libère le défilement — doivent être faits quand l'événement rend la main, pas au rendu
    // suivant.
    const avantImpression = () => flushSync(onFermer)
    window.addEventListener('keydown', touche)
    document.addEventListener('focusin', sortie)
    window.addEventListener('beforeprint', avantImpression)
    return () => {
      window.removeEventListener('keydown', touche)
      document.removeEventListener('focusin', sortie)
      window.removeEventListener('beforeprint', avantImpression)
      libererDefilement()
      // Remplacée par une autre fenêtre, celle-ci ne reprend pas le focus à la nouvelle.
      if (fermerOuverte !== onFermer) return
      fermerOuverte = null
      if (rendreFocus) boutonRetour?.focus()
    }
  }, [ouvert, onFermer, retour])

  const surVoile = (e: MouseEvent) => e.target === e.currentTarget || e.target === barre.current

  return (
    <div ref={fenetre} className={ouvert ? 'obs2-pe' : 'obs2-pe-repos'}
      role={ouvert ? 'dialog' : undefined} aria-modal={ouvert || undefined}
      aria-label={ouvert ? `${titre} — plein écran` : undefined}
      tabIndex={ouvert ? -1 : undefined}
      onMouseDown={ouvert ? e => { debutSurVoile.current = surVoile(e) } : undefined}
      onClick={ouvert ? e => {
        if (debutSurVoile.current && surVoile(e)) onFermer()
        debutSurVoile.current = false
      } : undefined}>
      {/* La barre se pose AVANT la carte, sur le voile : la carte garde sa place parmi les
          enfants du cadre (aucun remontage), et le coin de la carte reste à l'épingle et au
          partage. */}
      {ouvert && (
        <div ref={barre} className="obs2-pe-barre">
          <button ref={boutonFermer} type="button" className="obs2-pe-fermer"
            aria-label="Fermer le plein écran" onClick={onFermer}>
            <X size={16} aria-hidden="true" />
            Fermer
          </button>
        </div>
      )}
      {children}
    </div>
  )
}
